import { RateCalendarStore } from "stores/rateCalendar/RateCalendarStore";
import { SerializedData, Store } from "bernie-core";
import { NewDatePickerContent } from "uitk-react-new-date-picker/dist/dependencies/new-date-picker-content-interface";
import { RateCalendarFooterProps } from "components/flexComponents/WizardHotelPWA/components/RateCalendarFooter";
import { DestinationPricingService } from "services/DestinationPricingService";
import { Logger, NOOP_LOGGER, SystemEvent, SystemEventLevel } from "bernie-logger";
import { ExtendedContextStore, FlexContext } from "typings/flexFramework/FlexDefinitions";
import { Experiment, getExperimentBucket } from "components/utility/experiment";
import { ILocalization } from "bernie-l10n";
import { Hotel } from "typings/microserviceModels/hotels-flex-module";

export class ServerRateCalendarStore extends Store implements RateCalendarStore {
  public experimentBucket: number;
  public isExperimentEnabled: boolean;
  public datePickerContent: NewDatePickerContent;
  public footerHtmlProps: RateCalendarFooterProps;
  public footerText: string = "";

  public destinationPricingService: DestinationPricingService;
  public l10n: ILocalization;

  constructor(
    destinationPricingService: DestinationPricingService,
    l10n: ILocalization,
    state: SerializedData = {},
    logger: Logger = NOOP_LOGGER
  ) {
    super(state, logger);
    this.destinationPricingService = destinationPricingService;
    this.l10n = l10n;
  }

  public hydrate(data: SerializedData): void {
    // does nothing
  }

  /**
   * removes data that will be re-constructed client-side
   * @returns SerializedData
   */
  public toJSON(): SerializedData {
    return {
      experimentBucket: this.experimentBucket,
      isExperimentEnabled: this.isExperimentEnabled,
      datePickerContent: this.datePickerContent,
      footerHtmlProps: this.footerHtmlProps,
      footerText: this.footerText,
    };
  }

  /**
   * Initializes RateCalendarStore with data from other stores.
   * @param  { FlexContext } flexContext
   * @param  { ExtendedContextStore } contextStore
   * @param  { Hotel[] } hotels
   * @returns void
   */
  public async setup(flexContext: FlexContext, contextStore: ExtendedContextStore, hotels: Hotel[]) {
    this.setExperimentBucket(contextStore);
    this.isExperimentEnabled = this.experimentBucket !== 0;

    if (!flexContext || !this.isExperimentEnabled) {
      return;
    }

    if (hotels.length < 20) {
      this.logger.logEvent(new SystemEvent(SystemEventLevel.WARN, "RATE_CALENDAR_SETUP"), "Not enough hotels.");

      return;
    }

    try {
      const response = await this.destinationPricingService.fetchRateCalendar(hotels, flexContext, contextStore);

      if (response === undefined) {
        this.logger.logEvent(
          new SystemEvent(SystemEventLevel.WARN, "RATE_CALENDAR_SETUP"),
          "Empty response from DestinationPricingService."
        );

        return;
      }

      this.setDatePickerContent(response);
      this.setFooterHtmlProps();
      this.setFooterText();
    } catch (error) {
      this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, "RATE_CALENDAR_SETUP"), error);
    }
  }

  public setFooterHtmlProps() {
    const props = { displayPositive: false, displayNegative: false };

    if (this.datePickerContentIsEmpty() || !this.isExperimentEnabled) {
      this.footerHtmlProps = props;

      return;
    }

    if (this.experimentBucket === 1) {
      props.displayNegative = true;
    } else if (this.experimentBucket === 2) {
      props.displayPositive = true;
    } else if (this.experimentBucket === 3) {
      props.displayNegative = true;
      props.displayPositive = true;
    }

    this.footerHtmlProps = props;
  }

  public setFooterText() {
    if (this.experimentBucket !== 0 && !this.datePickerContentIsEmpty()) {
      this.footerText = this.l10n.formatText("wizard.ratecalendar.footer.legend.legal", 20);
    }
  }

  private setExperimentBucket(contextStore: ExtendedContextStore) {
    this.experimentBucket = getExperimentBucket(
      contextStore,
      Experiment.Destination_Pricing_Rate_Calendar_HTG_Filter_Wizard
    );
    this.isExperimentEnabled = this.experimentBucket !== 0;
  }

  private setDatePickerContent(datePickerContent: NewDatePickerContent) {
    this.datePickerContent = datePickerContent;
  }

  private datePickerContentIsEmpty(): boolean {
    return !this.datePickerContent || Object.keys(this.datePickerContent).length === 0;
  }
}
